import { ExternalLink, GitCommit, GitPullRequest, Rocket, ScrollText } from 'lucide-react'
import type { ReactNode } from 'react'
import {
  useBosunProjectReleases,
  type BosunProjectRead,
  type GitHubReleaseRead,
} from '@/api/queries'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { formatError } from '@/lib/format-error'
import { formatLocalDateTime } from '@/lib/format-date'
import { cn } from '@/lib/utils'

function shortSha(value: string | null | undefined): string {
  return value ? value.slice(0, 7) : 'unknown'
}

function ReleaseLink({
  href,
  children,
}: {
  href: string | null | undefined
  children: ReactNode
}) {
  if (!href) return <span className="text-muted-foreground">Missing</span>
  return (
    <button
      type="button"
      onClick={() => window.open(href, '_blank', 'noopener,noreferrer')}
      className="inline-flex min-w-0 items-center gap-1 text-primary hover:underline"
    >
      <span className="truncate">{children}</span>
      <ExternalLink className="size-3 shrink-0" />
    </button>
  )
}

function ReleaseSummary({ release }: { release: GitHubReleaseRead }) {
  const commits = release.shipped_commits.slice(0, 5)
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">
            {release.name || release.tag_name || 'Latest production release'}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {release.created_at ? formatLocalDateTime(release.created_at) : 'Date unavailable'}
          </p>
        </div>
        {release.tag_name ? <Badge tone="success">{release.tag_name}</Badge> : null}
      </div>

      <div className="grid gap-2 text-xs sm:grid-cols-3">
        <div className="min-w-0 rounded-md border border-white/10 bg-black/20 p-2">
          <span className="flex items-center gap-1 text-muted-foreground">
            <GitPullRequest className="size-3.5" />
            Release PR
          </span>
          <p className="mt-1 truncate font-medium">
            <ReleaseLink href={release.release_pr_url}>
              {release.release_pr_number ? `#${release.release_pr_number}` : 'Open'}
            </ReleaseLink>
          </p>
        </div>
        <div className="min-w-0 rounded-md border border-white/10 bg-black/20 p-2">
          <span className="flex items-center gap-1 text-muted-foreground">
            <GitCommit className="size-3.5" />
            Deployed SHA
          </span>
          <p className="mt-1 font-mono text-[11px] font-medium">
            {shortSha(release.deployed_sha)}
          </p>
        </div>
        <div className="min-w-0 rounded-md border border-white/10 bg-black/20 p-2">
          <span className="flex items-center gap-1 text-muted-foreground">
            <Rocket className="size-3.5" />
            Deploy run
          </span>
          <p className="mt-1 truncate font-medium">
            <ReleaseLink href={release.deploy_run_url}>Actions</ReleaseLink>
          </p>
        </div>
      </div>

      {commits.length > 0 ? (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">Shipped commits</p>
          <ul className="space-y-1">
            {commits.map((commit) => (
              <li key={commit.sha} className="flex min-w-0 items-start gap-2 text-xs">
                <span className="mt-0.5 shrink-0 rounded border border-white/10 bg-white/[0.04] px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                  {commit.short_sha}
                </span>
                <ReleaseLink href={commit.html_url}>{commit.message}</ReleaseLink>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">No shipped commits listed yet.</p>
      )}
    </div>
  )
}

export function ProjectReleaseNotesPanel({
  workspaceUuid,
  project,
  className,
}: {
  workspaceUuid: string
  project: BosunProjectRead
  className?: string
}) {
  const { data: releases, isLoading, error } = useBosunProjectReleases(workspaceUuid, project.uuid)
  const latest = releases?.[0]

  return (
    <section className={cn('rounded-lg border border-white/10 bg-white/[0.025] p-3', className)}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-semibold">
            <ScrollText className="size-4 text-primary" />
            Release notes
          </p>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {project.slug}
          </p>
        </div>
        {latest?.html_url ? (
          <ReleaseLink href={latest.html_url}>GitHub</ReleaseLink>
        ) : null}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : error ? (
        <Alert tone="warning">
          <AlertDescription>{formatError(error)}</AlertDescription>
        </Alert>
      ) : latest ? (
        <ReleaseSummary release={latest} />
      ) : (
        <p className="text-sm text-muted-foreground">No GitHub Releases found for this project.</p>
      )}
    </section>
  )
}
